import { ComponentList } from '../../components/core/component-list.js';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModelBuilder, cloneData, cloneDataOrNull, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PhoneNumber } from './phone-number.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { PartnerProfileBase, PartnerProfileStatus } from '../partner-profile-base.js';
import { fromEnumAsInt, fromEnumAsString, toEnumFromInt, toEnumFromString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';
import { MembershipRole } from './membership-role.js';

export class PromoterProfile extends PartnerProfileBase {
  static readonly TYPE_ID: string = '5f79aded1fc84665af3bae02b31d06f3';
  static readonly SCHEMA_VERSION: number = 3;

  public readonly formattedName: string;
  public readonly initials: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly phoneNumber: PhoneNumber,
    public readonly photo: PhotoReference | null,
    public readonly userId: string | null,
    public readonly pushNotificationToken: string | null,
    public readonly email: string | null,
    public readonly status: PartnerProfileStatus,
    public readonly partnerRole: MembershipRole,
    public readonly components: ComponentList,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName)!;
    this.initials = getInitialsFromName(firstName, lastName)!;
  }

  get $typeId(): string {
    return PromoterProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PromoterProfile.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    firstName: string,
    lastName: string,
    phoneNumber: PhoneNumber,
    photo: PhotoReference | null,
    userId: string | null,
    pushNotificationToken: string | null,
    email: string | null,
    status: PartnerProfileStatus,
    partnerRole: MembershipRole,
    components: ComponentList,
  }): PromoterProfile {
    return new PromoterProfile(
        args.uniqueId,
        args.businessRef,
        args.firstName,
        args.lastName,
        args.phoneNumber,
        args.photo,
        args.userId,
        args.pushNotificationToken,
        args.email,
        args.status,
        args.partnerRole,
        args.components,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PromoterProfile {
    return new PromoterProfile(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'firstName'),
        MapUtils.get<string>(map, 'lastName'),
        PhoneNumber.mustDecode(MapUtils.get<string>(map, 'phoneNumber')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'photo')),
        MapUtils.getOrNull<string>(map, 'userId'),
        MapUtils.getOrNull<string>(map, 'pushNotificationToken'),
        MapUtils.getOrNull<string>(map, 'email'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), PartnerProfileStatus),
        toEnumFromInt(MapUtils.get<number>(map, 'partnerRole'), MembershipRole),
        ComponentList.deserialize(context, MapUtils.getRaw(map, 'components') as SerializedData[]),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'firstName': this.firstName,
      'lastName': this.lastName,
      'phoneNumber': this.phoneNumber.encode(),
      'photo': context.toDataOrNull(this.photo),
      'userId': this.userId,
      'pushNotificationToken': this.pushNotificationToken,
      'email': this.email,
      '$status': fromEnumAsString(this.status),
      'partnerRole': fromEnumAsInt(this.partnerRole),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    firstName = this.firstName,
    lastName = this.lastName,
    phoneNumber = cloneData(this.phoneNumber),
    photo = cloneDataOrNull(this.photo),
    userId = this.userId,
    pushNotificationToken = this.pushNotificationToken,
    email = this.email,
    status = this.status,
    partnerRole = this.partnerRole,
    components = cloneData(this.components),
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    firstName?: string,
    lastName?: string,
    phoneNumber?: PhoneNumber,
    photo?: PhotoReference | null,
    userId?: string | null,
    pushNotificationToken?: string | null,
    email?: string | null,
    status?: PartnerProfileStatus,
    partnerRole?: MembershipRole,
    components?: ComponentList,
  } = {}): this {
    return new PromoterProfile(
        uniqueId,
        businessRef,
        firstName,
        lastName,
        phoneNumber,
        photo,
        userId,
        pushNotificationToken,
        email,
        status,
        partnerRole,
        components,
    ) as this;
  }

  isSame(other: PromoterProfile): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.businessRef === this.businessRef &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.photo, this.photo) &&
        other.userId === this.userId &&
        other.pushNotificationToken === this.pushNotificationToken &&
        other.email === this.email &&
        other.status === this.status &&
        other.partnerRole === this.partnerRole &&
        other.components.isSame(this.components);
  }
}

export class PromoterProfileBuilder extends DataModelBuilder<PromoterProfile> {
  build(context: SerializationContext, map: SerializedData): PromoterProfile {
    return PromoterProfile.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: PromoterProfile): SerializedData | null {
    return { 'formattedName': model.formattedName };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(PromoterProfile.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(PromoterProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [isPublic] of type [bool] was removed.
    if(version < 2) {
      version = 2;
    }

    // In this version `partnerRole` field was added.
    if(version < 3) {
      version = 3;
      map['partnerRole'] = fromEnumAsInt(MembershipRole.Collaborator);
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
